import { noop } from 'lodash';
import {
  OptionsWithExtraProps,
  SnackbarKey,
  SnackbarMessage,
  VariantType,
} from 'notistack';
import { createContext } from 'react';

import { ButtonProps } from '@/components/form/baseInputs/Button';

type ExposedNotIStackOpts = Pick<
  OptionsWithExtraProps<VariantType>,
  'autoHideDuration' | 'persist' | 'variant'
>;

export type FeedbackPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface FeedbackOptions extends ExposedNotIStackOpts {
  position?: FeedbackPosition;
  alertTargetUrl?: string;
  alertActionButton?: {
    label: string;
    // Partial so callers only need to override the fields they care about —
    // Alert.tsx already supplies sensible defaults (`variant="primary"`,
    // `size="xs"`) before spreading.
    buttonProps?: Partial<ButtonProps>;
  };
}

// Shortcut, commonly used in apollo onError onSuccess hooks
export type FeedbackFactoryFn = (
  message: SnackbarMessage,
  opts?: Omit<FeedbackOptions, 'message' | 'variant'>
) => () => void;

export type ShowFeedbackFn = (
  message: SnackbarMessage,
  opts?: FeedbackOptions
) => SnackbarKey;
export type HideFeedbackFn = (key?: SnackbarKey) => void;
export interface FeedbackHook {
  createErrorFeedback: FeedbackFactoryFn;
  createSuccessFeedback: FeedbackFactoryFn;
  createWarningFeedback: FeedbackFactoryFn;
  showFeedback: ShowFeedbackFn;
  hideFeedback: HideFeedbackFn;
}

export const FeedbackContext = createContext<FeedbackHook>({
  createErrorFeedback: () => noop,
  createSuccessFeedback: () => noop,
  createWarningFeedback: () => noop,
  showFeedback: () => '',
  hideFeedback: noop,
});
